import { create, StateCreator } from "zustand";
import { createJSONStorage, persist, PersistOptions } from 'zustand/middleware';
import { ICart, ICategory, IDetail, IItems, IMenu, IModifierGroup, menuItem, promotionItem } from '../types/menu';
import { ITables } from "../types/store";
import moment from "moment";
import { getCategories, getItemDetail, getItems, getMenus } from "../services/menu.service";
import { IAyceItem, IAyceMenu, useOrderStore } from "./order";
import { isBetweenHours, getCurrentDayIndex } from '../utils/timeUtils';
import { menuLog, timerLog, devLog, logger } from '../utils/logger';

interface ITotalOrderTimeLimit {
  useAyceTimeLimit: boolean;
  ayceTimeLimitMins: number;
}

export enum TimerState {
  RUNNING = 'RUNNING',
  STOPPED = 'STOPPED',
  FINISHED = 'FINISHED',
  RESET = 'RESET',
}

type MenuStore = {
  currentPage: string;
  setCurrentPage: (value: string) => void;
  selectedTable: ITables;
  setSelectedTable: (value: ITables) => void;
  selectedMenu: IMenu | undefined;
  setSelectedMenu: (value: IMenu) => void;
  checkUseHour: boolean;
  // updateCheckOpsHours: () => void;
  selectedCategory: ICategory | undefined;
  setSelectedCategory: (category: ICategory | undefined) => void;
  selectedItem: { itemId: number, categoryId: number } | null;
  setSelectedItem: (value: { itemId: number, categoryId: number } | null) => void;
  itemDetail: IDetail | null;
  setItemDetail: (value: IDetail | null) => void;
  isPromotion: boolean;
  setIsPromotion: (value: boolean) => void;
  promotionList: number;
  setPromotionList: (value: number) => void;
  idleState : boolean;
  setIdleState: (value:boolean) => void;
  isIdle: boolean;
  setIsIdle : (value:boolean) => void;
  isNativeFlag: boolean;
  setIsNativeFlag : (value:boolean) => void;
  askServerClicked: boolean;
  setAskServerClicked: (value: boolean) => void;
  totalOrderTimeLimit: ITotalOrderTimeLimit;
  setTotalOrderTimeLimit: (value: ITotalOrderTimeLimit) => void;
  // Time: number;
  remainingOrderTime: number;
  setRemainingOrderTime: (value: number) => void;
  decrementRemainingOrderTime: () => void;
  resetRemainingOrderTime: () => void;
  restartTimer: () => void; // 💡 새로 추가
  isTimerRunning: boolean;
  startTimer: () => void;
  stopTimer: () => void;
  resetPopup: () => void;
  resetMenu: () => void;
  categories: ICategory[];
  setCategories: (value: ICategory[]) => void;
  resetTimer: () => void;
  // Android Native Menu Data
  menuDao: menuItem[];
  setMenuDao: (value: menuItem[]) => void;
  // Android Native Promotion Data
  promotionDao: promotionItem[];
  setPromotionDao: (value: promotionItem[]) => void;
  fetchMenus: (userInfo: any, storeId: number, tableId: number) => Promise<void>;
  fetchCategories: (storeId: string) => Promise<void>;
  fetchItems: (categoryId: number) => Promise<void>;
  fetchItemDetail: (itemId:number, categoryId: number, tableId:number) => Promise<void>;
  setTopMenus: (topMenus: IMenu[]) => void;
  setItems: (items: IItems) => void;
  timerInfoMap: Map<number, any>;
  setTimerInfo: (categoryId: number, info: any) => void;
  deleteTimerInfo: (categoryId: number) => void;
  // localTime: { time: string, timezone: string, lastUpdated: string };
  // setLocalTime: (time: string, timezone: string) => void; 
  setVisibleMenus: () => void;
  ayceMenus: IAyceMenu[];
  setAYCEMenus: (ayceMenus: IAyceMenu[]) => void;
  setVisibleAYCEMenus: () => void;
  topMenus: IMenu[];
  items: IItems;
  updateCategoryTimer: (categoryId: number, timerInfo: Partial<ICategory>) => void;
  updateAllTimers: () => void;
  lastRestartTime?: number;
  timerState: TimerState;
  setTimerState: (state: TimerState) => void;
  loadingStates: { [key: string]: boolean };
  setLoadingStates: (loadingStates: { [key: string]: boolean }) => void;
}

type MyPersist = (
  config: StateCreator<MenuStore>,
  options: PersistOptions<MenuStore>
) => StateCreator<MenuStore>

type State = {
  currentPage: string,
  selectedCategory: ICategory | undefined;
  selectedItem: { itemId: number, categoryId: number } | null;
  itemDetail: IDetail | null;
  categories: ICategory[];
  isIdle: boolean;
  isNativeFlag: boolean;
  askServerClicked: boolean;
  topMenus: IMenu[];
  items: IItems;
  timerInfoMap: Map<number, any>;
  totalOrderTimeLimit: ITotalOrderTimeLimit;
  remainingOrderTime: number;
  isTimerRunning: boolean;
  ayceMenus: IAyceMenu[];
  selectedMenu: IMenu | undefined;
  // localTime: { time: string, timezone: string, lastUpdated: string };
  timerState: TimerState;
  loadingStates: { [key: string]: boolean };
}

const initialState: State = {
  currentPage: '',
  selectedCategory: undefined,
  selectedItem: null,
  itemDetail: null,
  categories: [],
  isIdle: false,
  isNativeFlag: false,
  askServerClicked: false,
  topMenus: [],
  // localTime: { time: '', timezone: '', lastUpdated: '' },
  items: {
    categoryId: 0,
    data: [],
    index: 0
  },
  timerInfoMap: new Map(),
  totalOrderTimeLimit: {useAyceTimeLimit: false, ayceTimeLimitMins: 0},
  remainingOrderTime: 0 * 60,
  isTimerRunning: false,
  ayceMenus: [],
  timerState: TimerState.STOPPED,
  selectedMenu: undefined,
  loadingStates: {},
}

export const useMenuStore = create<MenuStore>(
  (persist as MyPersist)(
    (set, get): MenuStore => ({
      selectedTable: { id: 0, tableName: '', posTableId: 0, isKioskMode: false } as ITables,
      isPromotion: false,
      checkUseHour: true,
      promotionList: -1,
      idleState: false,
      menuDao: [] as menuItem[],
      promotionDao: [] as promotionItem[],
      ...initialState,
      setCurrentPage: (value: string) => set({currentPage: value}),
      setTopMenus: (topMenus: IMenu[]) => set({ topMenus: topMenus }),
      setCategories: (value: ICategory[]) => {
        if (Array.isArray(value) && value.every(category => typeof category === 'object' && category.id)) {
          // 기존 카테고리의 타이머 정리
          const currentCategories = get().categories;
          
          // 모든 타이머 제거
          currentCategories.forEach(item => {
            if (item.timeoutId) {
              clearTimeout(item.timeoutId);
            }
            if (item.intervalId) {
              clearInterval(item.intervalId);
            }
          });
          
          // 새 카테고리 설정 시 타이머 관련 속성을 제거하지 않도록 변경
          const preservedCategories = value.map(category => {
            return { ...category };
          });
          
          menuLog(`Categories updated: ${value.length} categories`);
          
          set({ categories: preservedCategories });
        } else {
          logger.error('Invalid categories value:', value);
        }
      },
      setAYCEMenus: (ayceMenus: IAyceMenu[]) => set({ ayceMenus: ayceMenus }),
            setVisibleAYCEMenus: () => {
        logger.log('🔄 setVisibleAYCEMenus called');
        const state = get();
        
        logger.log(`[setVisibleAYCEMenus] Processing ${state.ayceMenus.length} AYCE menus`);
        
        /** 1) 현재 시간을 한 번만 계산하여 모든 메뉴에 동일한 시간 적용 */
        const currentTime = moment();
        logger.log(`[setVisibleAYCEMenus] Using consistent time: ${currentTime.format('YYYY-MM-DD HH:mm:ss')}`);
        
        /** 2) 변경된 메뉴만 업데이트 (성능 최적화) */
        let hasChanges = false;
        const updatedAyceMenus = state.ayceMenus.map(menu => {
          const newVisible = checkOpsHours(menu, currentTime);
          
          logger.debug(`[setVisibleAYCEMenus] Menu ${menu.id}: current visible=${menu.visible}, new visible=${newVisible}`);
          
          // visible 상태가 변경되지 않았으면 기존 객체 재사용
          if (menu.visible === newVisible) {
            return menu;
          }
          
          // 변경된 경우에만 새 객체 생성
          hasChanges = true;
          logger.debug(`[setVisibleAYCEMenus] Menu ${menu.id} visibility changed from ${menu.visible} to ${newVisible}`);
          return { ...menu, visible: newVisible };
        });

        // 변경사항이 없으면 상태 업데이트 하지 않음
        if (!hasChanges) {
          logger.log('[setVisibleAYCEMenus] No changes detected, skipping update');
          return;
        }
        
        // 변경된 메뉴만 로그 출력
        const changedMenus = updatedAyceMenus.filter((menu, index) => 
          state.ayceMenus[index]?.visible !== menu.visible
        );
        
        if (changedMenus.length > 0) {
          menuLog(`Changed AYCE menus: ${changedMenus.map(m => `${m.menuName}(${m.visible})`).join(', ')}`);
        }
        
        set({
          ayceMenus: updatedAyceMenus
        });
      },
      setVisibleMenus: () => {
        const { topMenus, selectedMenu } = get();
        
        logger.log(`[setVisibleMenus] Processing ${topMenus.length} menus`);
        
        /** 1) 현재 시간을 한 번만 계산하여 모든 메뉴에 동일한 시간 적용 */
        const currentTime = moment();
        logger.log(`[setVisibleMenus] Using consistent time: ${currentTime.format('YYYY-MM-DD HH:mm:ss')}`);
        
        /** 2) 변경된 메뉴만 업데이트 (성능 최적화) */
        let hasChanges = false;
        const updatedMenus = topMenus.map((menu) => {
          const newVisible = checkOpsHours(menu, currentTime);
          
          logger.debug(`[setVisibleMenus] Menu ${menu.id}: current visible=${menu.visible}, new visible=${newVisible}`);
          
          // visible 상태가 변경되지 않았으면 기존 객체 재사용
          if (menu.visible === newVisible) {
            return menu;
          }
          
          // 변경된 경우에만 새 객체 생성
          hasChanges = true;
          logger.debug(`[setVisibleMenus] Menu ${menu.id} visibility changed from ${menu.visible} to ${newVisible}`);
          return { ...menu, visible: newVisible };
        });

        // 변경사항이 없으면 상태 업데이트 하지 않음
        if (!hasChanges) {
          logger.log('[setVisibleMenus] No changes detected, skipping update');
          return;
        }

        /** 2) 현재 선택된 메뉴가 여전히 노출 가능한가? */
        const stillVisible = selectedMenu
          ? updatedMenus.find((m) => m.id === selectedMenu.id)?.visible
          : false;

        /** 3) 아니라면 '맨 앞의 visible 메뉴'(없으면 undefined)로 대체 */
        const nextSelected =
          stillVisible
            ? selectedMenu
            : updatedMenus.find((m) => m.visible) ?? undefined;

        /** 4) 변경된 메뉴만 로그 출력 */
        const changedMenus = updatedMenus.filter((menu, index) => 
          topMenus[index]?.visible !== menu.visible
        );
        
        if (changedMenus.length > 0) {
          menuLog(`Changed menus: ${changedMenus.map(m => `${m.id}(${m.visible})`).join(', ')}`);
        }

        /** 5) 한 번에 상태 갱신 */
        const useHour = checkOpsHours(nextSelected);
        set({
          topMenus: updatedMenus,
          selectedMenu: nextSelected || undefined,
          checkUseHour: useHour
        });
      },
      // setLocalTime: (time, timezone) => {
      //   set({ localTime: { time, timezone, lastUpdated: new Date().toISOString() } });
    
      //   // const now = moment.utc(time).tz(timezone);
      //   console.log("setLocalTime", get().topMenus, time, timezone)
      //   const updatedMenus = get().topMenus.map((menu) => {
      //     // ❷ selectedMenu 자리에 menu를 넣어 check
      //     const tmpState = { ...get(), selectedMenu: menu };
      //     const visible = checkOpsHours(tmpState);   // true / false
      //     return { ...menu, visible };               // ❸ visible 필드 반영
      //   });
        
      //   // ❹ 한 번에 상태 갱신 → React는 필요한 컴포넌트만 리렌더
      //   set({ topMenus: updatedMenus });
      // },
      setItems: (items: IItems) =>  set({ items: items}),
      setSelectedTable: (table: ITables) => {
        if (table && typeof table === 'object' && 'id' in table) {
          set({ selectedTable: table });
        } else {
          logger.error('Invalid selectedTable value:', table);
        }
      },

      setSelectedMenu: (menu: IMenu) => {
        if (!menu?.id || !Array.isArray(menu.menuHours)) {
          logger.error("Invalid menu data:", menu);
          return;
        }

        const useHour = checkOpsHours( menu );
        
        set({ selectedMenu: menu, checkUseHour: useHour });
      },
      setMenuDao: (menuDao: menuItem[]) => {
        if (
          Array.isArray(menuDao) &&
          menuDao.every(
            (item) =>
              typeof item.id === "number" &&
              Array.isArray(item.images) &&
              item.images.every((image) => typeof image === "string" || image === null)
          )
        ) {
          set({ menuDao });
        } else {
          logger.error("Invalid menuDao value:", menuDao);
        }
      },
      setPromotionDao: (promotionDao: promotionItem[]) => {
        if (
          Array.isArray(promotionDao) &&
          promotionDao.every(
            (item) =>
              typeof item.id === "number" && typeof item.mediaPath === "string"
          )
        ) {
          set({ promotionDao });
        } else {
          logger.error("Invalid promotionDao value:", promotionDao);
        }
      },
      // updateCheckOpsHours: () => {
      //   const state = get();
      //   const useHour = checkOpsHours(state);
      //   console.log(`Updating checkOpsHours: ${useHour}, current time: ${moment().format()}`);
      //   set({ checkUseHour: useHour });
      // },
      setSelectedCategory: (category: ICategory | undefined) => {
        if (category && typeof category === 'object' && 'id' in category) {
          set({ selectedCategory: category });
        } else {
          logger.error('Invalid selectedCategory value:', category);
        }
      },
      setSelectedItem: (item: { itemId: number, categoryId: number } | null) => {
        logger.log('🔍 setSelectedItem called with:', item);
        set({ selectedItem: item });
        logger.log('🔍 selectedItem state updated to:', item);
      },
      setItemDetail: (detail: IDetail | null) => {
        set({ itemDetail: detail });
      },
      setIsPromotion: (value: boolean) => {
        if (typeof value === 'boolean') {
          set({ isPromotion: value });
        } else {
          logger.error('Invalid isPromotion value:', value);
        }
      },
      setPromotionList: (value: number) => {
        if (typeof value === 'number') {
          set({ promotionList: value });
        } else {
          logger.error('Invalid promotionList value:', value);
        }
      },
      setIdleState: (value: boolean) => {
        if (typeof value === 'boolean') {
          set({ idleState: value });
        } else {
          logger.error('Invalid idleState value:', value);
        }
      },
      setIsIdle: (value: boolean) => {
        if (typeof value === 'boolean') {
          set({ isIdle: value });
        } else {
          logger.error('Invalid idIdle value:', value);
        }
      },
      setIsNativeFlag: (value: boolean) => {
        if (typeof value === 'boolean') {
          set({ isNativeFlag: value });
        } else {
          logger.error('Invalid isNativeFlag value:', value);
        }
      },
      setAskServerClicked: (value: boolean) => {
        if (typeof value === 'boolean') {
          set({ askServerClicked: value });
        } else {
          logger.error('Invalid askServerClicked value:', value);
        }
      },
      setTotalOrderTimeLimit: (value: ITotalOrderTimeLimit) => {
        logger.log('setTotalOrderTimeLimit', value)
        const ayceTimeLimitMins = value.useAyceTimeLimit ? value.ayceTimeLimitMins * 60 : 0;
        const useAyceTimeLimit = value.useAyceTimeLimit;
        set({ totalOrderTimeLimit: { ayceTimeLimitMins, useAyceTimeLimit }});

      },
      setRemainingOrderTime: (value: number) => {
        if (typeof value === 'number' && value >= 0) {
          set({ remainingOrderTime: value });
        } else {
          logger.error('Invalid remainingOrderTime value:', value);
        }
      },
      decrementRemainingOrderTime: () => set((state) => {
        logger.log('🕐 [decrementRemainingOrderTime] Before decrement:', state.remainingOrderTime, 'seconds')
        
        let newTime: number;
        if (state.remainingOrderTime > 60) {
          // 1분 이상 남았을 때: 60초씩 감소
          newTime = state.remainingOrderTime - 60;
        } else {
          // 1분 미만 남았을 때: 정확히 00:00에 도달하도록 계산
          newTime = 0;
        }
        
        newTime = Math.max(0, newTime);
        const newTimerState = newTime === 0 ? TimerState.FINISHED : state.timerState;
        
        // 화면 깜빡임 방지를 위해 상태 변경 최소화
        if (state.remainingOrderTime === newTime && state.timerState === newTimerState) {
          return state; // 변경사항이 없으면 상태 업데이트 안함
        }
        
        return {
          remainingOrderTime: newTime,
          timerState: newTimerState
        };
      }),
      resetRemainingOrderTime: () => {
        const state = get();

        set((state) => ({
          remainingOrderTime: state.totalOrderTimeLimit.ayceTimeLimitMins, // 초 단위로 직접 사용
          timerState: TimerState.RESET
        }));
      },
      restartTimer: () => {
        set((state) => {
          const now = Date.now();
          const ayceTimeLimitMins = state.totalOrderTimeLimit.ayceTimeLimitMins;
          
          logger.log('🕐 [restartTimer] Called with ayceTimeLimitMins (seconds):', ayceTimeLimitMins)
          logger.log('🕐 [restartTimer] Current remainingOrderTime:', state.remainingOrderTime)
          logger.log('🕐 [restartTimer] Current timer state:', state.timerState)
          logger.log('🕐 [restartTimer] Is timer running:', state.isTimerRunning)
          
          if (state.timerState === TimerState.RUNNING && state.remainingOrderTime > 0) {
            if (state.lastRestartTime && (now - state.lastRestartTime < 5000)) {
              logger.log('🕐 [restartTimer] Too soon to restart, skipping')
              return state;
            }
            
            if (state.remainingOrderTime > (ayceTimeLimitMins * 0.9)) { // 초 단위로 비교
              logger.log('🕐 [restartTimer] Remaining time still high, skipping')
              return state;
            }
          }
          
          const newRemainingTime = ayceTimeLimitMins; // 초 단위로 직접 사용
          logger.log('🕐 [restartTimer] Setting new remainingOrderTime to:', newRemainingTime, 'seconds')
          
          return {
            ...state,
            remainingOrderTime: newRemainingTime, // 초 단위로 직접 사용
            isTimerRunning: true,
            timerState: TimerState.RUNNING,
            lastRestartTime: now
          };
        });
      },
      startTimer: () => {
        set((state) => {
          if (state.timerState === TimerState.RUNNING) {
            return state;
          }
          return { 
            ...state, 
            isTimerRunning: true,
            timerState: TimerState.RUNNING 
          };
        });
      },
      stopTimer: () => {
        set((state) => {
          if (state.timerState === TimerState.STOPPED) {
            return state;
          }
          if(state.remainingOrderTime === 0) {
            return { 
              ...state, 
              isTimerRunning: false,
              timerState: TimerState.FINISHED 
            };
          }
          return { 
            ...state, 
            isTimerRunning: false,
            timerState: TimerState.STOPPED 
          };
        });
      },
      //// load data
      fetchMenus: async (userInfo: { isAyce: boolean }, storeId: number, tableId: number) => {
        try {
          const response = await getMenus(userInfo, storeId, tableId);
          const data = response.data;
          
          if (!Array.isArray(data) || data.length === 0) {
            return;
          }

          const transformedData = data.map((item: IMenu) => ({
            ...item,
            visible: checkOpsHours(item),
            menuHours: Array.isArray(item.menuHours) ? item.menuHours : []
          }));

          // visible한 메뉴들 찾기
          const visibleMenus = transformedData.filter(menu => menu.visible);

          // 첫 번째 visible한 메뉴 선택 (없으면 첫 번째 메뉴)
          const firstVisibleMenu = visibleMenus.length > 0 ? visibleMenus[0] : transformedData[0];
                  const useHour = checkOpsHours(firstVisibleMenu);
          
          set({
            topMenus: transformedData,
            selectedMenu: firstVisibleMenu,
            checkUseHour: useHour
          });

          if (firstVisibleMenu?.id) {
            await get().fetchCategories(firstVisibleMenu.id.toString());
          }
        } catch (error) {
          logger.error("Error in fetchMenus:", error);
        }
      },
      fetchCategories: async (menuId: string) => {
        try {
          const response = await getCategories(menuId);
          const data = response.data;
          
          if (Array.isArray(data)) {
            set((state) => {
              // 새 카테고리에 기존 타이머 정보 병합
              const mergedCategories = data.map((newCat: ICategory) => {
                const savedTimerInfo = state.timerInfoMap.get(newCat.id);
                
                if (savedTimerInfo) {
                  // 타이머 만료 여부 확인
                  if (savedTimerInfo.timeoutEnd && savedTimerInfo.timeoutEnd < Date.now()) {
                    // 만료된 타이머 정보 삭제
                    state.timerInfoMap.delete(newCat.id);
                    return {
                      ...newCat,
                      isPreventSelection: false // 타이머 만료 시 선택 방지 모드 해제
                    };
                  }

                  // 남은 시간 재계산
                  const remainingTime = savedTimerInfo.timeoutEnd - Date.now();
                  const remainingMinutes = Math.ceil(remainingTime / 60000);

                  logger.log(`[Timer Debug] Category ${newCat.id} - Timer restored:`, {
                    originalEndTime: new Date(savedTimerInfo.timeoutEnd).toLocaleString(),
                    currentTime: new Date().toLocaleString(),
                    remainingMinutes
                  });

                  return {
                    ...newCat,
                    timeoutId: savedTimerInfo.timeoutId,
                    intervalId: savedTimerInfo.intervalId,
                    timeoutEnd: savedTimerInfo.timeoutEnd,
                    timerCategoryId: savedTimerInfo.timerCategoryId,
                    remainingMinutes,
                    isPreventSelection: true // 타이머가 있는 경우 선택 방지 모드 유지
                  };
                }
                return {
                  ...newCat,
                  isPreventSelection: false // 타이머가 없는 경우 선택 방지 모드 해제
                };
              });

              // 타이머 정보가 있는 카테고리만 로깅
              const categoriesWithTimers = mergedCategories.filter(cat => cat.timerCategoryId);
              if (categoriesWithTimers.length > 0) {
                logger.log('[Timer Debug] Categories with restored timers:', 
                  categoriesWithTimers.map(cat => ({
                    id: cat.id,
                    remainingMinutes: cat.remainingMinutes,
                    timerCategoryId: cat.timerCategoryId
                  }))
                );
              }

              return { 
                ...state, 
                categories: mergedCategories,
                selectedCategory: mergedCategories.length > 0 ? mergedCategories[0] : undefined,
                loading: false,
                error: null
              };
            });
          }
        } catch (error) {
          set((state) => ({ ...state, loading: false, error: error as Error }));
        }
      },
      fetchItems: async (categoryId: number) => {
        const response = await getItems(categoryId)
        const data = response.data;

        set({
          items: data,
        })
      },
      fetchItemDetail: async (itemId:number, categoryId: number, tableId:number) => {
        const startTime = performance.now();
        
        // 모바일/테블릿 환경 감지
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const isTablet = /iPad|Android(?=.*\bMobile\b)(?=.*\bSafari\b)/i.test(navigator.userAgent);
        
        logger.log(`📱 Device detected: ${isMobile ? 'Mobile' : isTablet ? 'Tablet' : 'Desktop'}`);
        
        // 이미 로딩 중인지 확인
        const currentItemDetail = get().itemDetail;
        const currentSelectedItem = get().selectedItem;
        
        // 같은 아이템이 이미 로드되어 있는지 확인 (Optimistic UI 데이터는 제외)
        if (currentItemDetail && 
            currentSelectedItem && 
            currentSelectedItem.itemId === itemId && 
            currentSelectedItem.categoryId === categoryId &&
            currentItemDetail.itemId === itemId &&
            currentItemDetail.modifierGroups && 
            currentItemDetail.modifierGroups.length > 0) {
          logger.log('🔍 Item detail already loaded, skipping API call');
          return;
        }
        
        // 로딩 중인지 확인하는 플래그 추가
        const loadingKey = `${itemId}-${categoryId}-${tableId}`;
        if (get().loadingStates && get().loadingStates[loadingKey]) {
          logger.log('🔍 Item detail already loading, skipping duplicate call');
          return;
        }
        
        // 로딩 상태 설정
        set((state) => ({
          loadingStates: { ...state.loadingStates, [loadingKey]: true }
        }));
        
        try {
          logger.log('🔍 fetchItemDetail called with:', { itemId, categoryId, tableId });
          
          const apiStartTime = performance.now();
          
          // 모바일/테블릿에서는 더 빠른 타임아웃 설정
          const timeout = isMobile || isTablet ? 10000 : 15000; // 모바일: 10초, 데스크톱: 15초
          
          const response = await getItemDetail(itemId, categoryId, tableId)
          const apiEndTime = performance.now();
          logger.log(`🔍 API call took ${(apiEndTime - apiStartTime).toFixed(2)}ms (${isMobile ? 'Mobile' : isTablet ? 'Tablet' : 'Desktop'})`);
          
          if (!response || !response.data) {
            logger.error('❌ fetchItemDetail: No response data');
            throw new Error('No response data from API');
          }
          
          const processStartTime = performance.now();
          // const data = response.data;
          if(response.data && response.data.modifierGroups) {
          
            response.data.modifierGroups = generatePaths(
              response.data.modifierGroups,
              `${itemId}`
            );

            response.data.modifierGroups?.forEach( (modifiers: { isRequired: any; }) => {
              if(modifiers.isRequired) {
                menuLog(modifiers.isRequired)
                // const addcheck = {checked: null}
                // options = Object.assign(options, addcheck);
                response.data.hasRequired = true 
              } 
            })
            const preOrderItem:ICart = {
              ...response.data,
              quantity: response.data.minRequired,
              newItem: true,
              categoryId: categoryId,
              itemWithModifierPrice: 0,
              orderModifiers: []
            }
            useOrderStore.getState().setPreOrder({...useOrderStore.getState().preOrder, ...preOrderItem,});
             logger.log('getDetail', response?.data)
            // calcHeight(response.data)
            useOrderStore.getState().resetOrder()
            useOrderStore.getState().setOrderPrice(response?.data.itemPrice)
            logger.log("fetchItemDetail", response.data)

            set({
              itemDetail: {...response?.data, quantity: response?.data.minRequired},
            })
          } else {
            logger.error('❌ fetchItemDetail: Invalid response data structure');
            throw new Error('Invalid response data structure');
          }
          
          const processEndTime = performance.now();
          logger.log(`🔍 Data processing took ${(processEndTime - processStartTime).toFixed(2)}ms`);
          
          const totalTime = performance.now() - startTime;
          logger.log(`✅ fetchItemDetail completed in ${totalTime.toFixed(2)}ms (${isMobile ? 'Mobile' : isTablet ? 'Tablet' : 'Desktop'})`);
          
        } catch (error) {
          const totalTime = performance.now() - startTime;
          logger.error(`❌ fetchItemDetail failed after ${totalTime.toFixed(2)}ms:`, error);
          // 에러 발생 시 itemDetail을 null로 설정하여 로딩 상태 표시
          set({ itemDetail: null });
          throw error; // 에러를 다시 던져서 호출자가 처리할 수 있도록 함
        } finally {
          // 로딩 상태 제거
          set((state) => {
            const newLoadingStates = { ...state.loadingStates };
            delete newLoadingStates[loadingKey];
            return { loadingStates: newLoadingStates };
          });
        }
      },
      /// reset
      resetPopup: () => set({ selectedItem: null }),
      resetMenu: () => set({ ...initialState }),
      resetTimer: () => {
        set((state) => {
          state.categories.forEach(item => {
            if (item.timeoutId) {
              clearTimeout(item.timeoutId);
            }
            if (item.intervalId) {
              clearInterval(item.intervalId);
            }
          });
          return { categories: [] };
        });
      },
      setTimerInfo: (categoryId: number, info: any) => {
        set((state) => {
          const currentMap = state.timerInfoMap instanceof Map ? state.timerInfoMap : new Map();
          const newMap = new Map(currentMap);
          newMap.set(categoryId, info);
          return { timerInfoMap: newMap };
        });
      },
      deleteTimerInfo: (categoryId: number) => {
        set((state) => {
          timerLog(`[Timer Debug] Deleting timer info for category ${categoryId}`);
          const currentMap = state.timerInfoMap instanceof Map ? state.timerInfoMap : new Map();
          const newMap = new Map(currentMap);
          newMap.delete(categoryId);
          timerLog(`[Timer Debug] Timer info deleted. Remaining timers:`, Array.from(newMap.keys()));
          return { timerInfoMap: newMap };
        });
      },
      updateCategoryTimer: (categoryId: number, timerInfo: Partial<ICategory>) => {
        set((state) => ({
          categories: state.categories.map(category => 
            category.id === categoryId ? { ...category, ...timerInfo } : category
          )
        }));
      },
      updateAllTimers: () => {
        set((state) => {
          const currentMap = state.timerInfoMap instanceof Map ? state.timerInfoMap : new Map();
          const newMap = new Map(currentMap);
          let hasChanges = false;
          let updatedCategories = [...state.categories];

          // 모든 타이머 체크
          for (const [categoryId, timerInfo] of newMap.entries()) {
            if (timerInfo.timeoutEnd && timerInfo.timeoutEnd < Date.now()) {
              // 타이머 만료
              newMap.delete(categoryId);
              hasChanges = true;
              
              // 카테고리 업데이트 - 모든 타이머 관련 속성 초기화
              updatedCategories = updatedCategories.map(category => 
                category.id === categoryId 
                  ? { 
                      ...category, 
                      isPreventSelection: false,
                      timeoutId: undefined,
                      intervalId: undefined,
                      timerCategoryId: undefined,
                      timeoutEnd: undefined,
                      remainingMinutes: undefined
                    }
                  : category
              );
            }
          }

          return hasChanges ? { 
            timerInfoMap: newMap,
            categories: updatedCategories
          } : state;
        });
      },
      setTimerState: (state: TimerState) => set({ timerState: state }),
      setLoadingStates: (loadingStates: { [key: string]: boolean }) => set({ loadingStates }),
    }),
    {
      name: 'menu',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => {
        const serializedMap = state.timerInfoMap instanceof Map 
          ? Array.from(state.timerInfoMap.entries())
          : [];
        return {
          ...state,
          timerInfoMap: serializedMap as unknown as Map<number, any>,
          remainingOrderTime: state.remainingOrderTime,
          isTimerRunning: state.isTimerRunning,
          totalOrderTimeLimit: state.totalOrderTimeLimit,
          lastRestartTime: state.lastRestartTime,
          timerState: state.timerState,
          loadingStates: state.loadingStates,
        };
      },
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.timerInfoMap = new Map(state.timerInfoMap);
          state.loadingStates = { ...state.loadingStates }; // Ensure loadingStates is also restored
          
          // 타이머 상태 복원 로직
          if (state.isTimerRunning && state.remainingOrderTime > 0) {
            const store = useMenuStore.getState();
            const now = Date.now();
            
            // 마지막 재시작 시간이 5초 이내면 타이머 상태 초기화
            if (state.lastRestartTime && (now - state.lastRestartTime < 5000)) {
              logger.log("Timer state reset on rehydrate - too soon since last restart");
              state.isTimerRunning = false;
              state.remainingOrderTime = state.totalOrderTimeLimit.ayceTimeLimitMins;
              state.lastRestartTime = undefined;
            } else {
              store.startTimer();
              logger.log("Timer state restored:", {
                remainingTime: state.remainingOrderTime,
                isRunning: state.isTimerRunning,
                lastRestart: state.lastRestartTime ? new Date(state.lastRestartTime).toLocaleTimeString() : 'never'
              });
            }
          } else {
            state.isTimerRunning = false;
            state.remainingOrderTime = state.totalOrderTimeLimit.ayceTimeLimitMins;
            state.lastRestartTime = undefined;
          }
        }
      }
    }
  )
);

// const WEEKDAY = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

function checkOpsHours(menu: any, currentTime?: moment.Moment): boolean {

  // >>>>>>>>>>>>> 기본 검증
  if (!menu?.id) {
    return false;
  }

  if (!Array.isArray(menu.menuHours) || menu.menuHours.length === 0) {
    return false;
  }

  // useHour가 false면 항상 true 반환
  if (!menu.useHour) {
    return true;
  }

  // moment 객체 재사용으로 메모리 효율성 향상 (전달받은 시간 사용, 없으면 현재 시간)
  const timeToCheck = currentTime || moment();
  
  // 먼저 임시로 현재 요일의 menuHour를 가져와서 endHour 확인
  const tempDayIndex = timeToCheck.day();
  const tempMenuHour = menu.menuHours[tempDayIndex];
  
  // 야간 영업시간을 고려한 올바른 요일 인덱스 계산
  const dayIndex = getCurrentDayIndex(timeToCheck, tempMenuHour?.endHour, tempMenuHour?.startHour);
  const menuHour = menu.menuHours[dayIndex];

  // 해당 요일의 영업시간이 없는 경우
  if (!menuHour) {
    return false;
  }

  // 하루 종일 영업인 경우
  if (menuHour.isOpenAllDay) {
    return true;
  }

  // 휴무일인 경우
  if (menuHour.isClosed) {
    return false;
  }

  // 영업시간 체크 (최적화된 함수 사용)
  const visible = isBetweenHours(
    timeToCheck,
    menuHour.startHour,
    menuHour.startMinute,
    menuHour.endHour,
    menuHour.endMinute
  );
  
  return visible;
}

const generatePaths = (groups: IModifierGroup[], parentPath: string = ''): IModifierGroup[] => {
  return groups.map((group, groupIndex) => {
    const groupPath = `${parentPath}/g-${group.id}-${groupIndex}`;
    return {
      ...group,
      isExpanded: false,
      path: groupPath,
      modifiers: group.modifiers.map((modifier, modifierIndex) => ({
        ...modifier,
        path: `${groupPath}/m-${modifier.id}-${modifierIndex}`,
        modifiers: modifier.modifiers
          ? generatePaths(modifier.modifiers, `${groupPath}/m-${modifier.id}-${modifierIndex}`)
          : [],
      })),
    };
  });
};

const restoreTimerForCategory = (categoryId: number, timerInfoMap: Map<number, any>, categories: ICategory[], setCategories: (categories: ICategory[]) => void) => {
  const timerInfo = timerInfoMap.get(categoryId);

  if (timerInfo) {
    logger.log(`Restoring timer for category ID: ${categoryId}`, timerInfo);

    // Find the category in the current categories
    const categoryIndex = categories.findIndex(cat => cat.id === categoryId);

    if (categoryIndex !== -1) {
      const updatedCategories = [...categories];
      const category = updatedCategories[categoryIndex];

      // Restore timer values
      updatedCategories[categoryIndex] = {
        ...category,
        timeoutId: timerInfo.timeoutId,
        intervalId: timerInfo.intervalId,
        timeoutEnd: timerInfo.timeoutEnd,
        timerCategoryId: timerInfo.timerCategoryId,
        remainingMinutes: timerInfo.remainingMinutes
      };

      setCategories(updatedCategories);
      logger.log(`Timer restored for category ID: ${categoryId}`);
    } else {
      logger.log(`Category ID: ${categoryId} not found`);
    }
  } else {
    logger.log(`No timer info found for category ID: ${categoryId}`);
  }
};
